import React, { useState, useEffect } from 'react';
import {
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  Divider,
  Chip,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Delete from '@mui/icons-material/Delete';
import { DataGrid } from '@mui/x-data-grid';
import { GridActionsCellItem } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import { GET, POST } from 'APIclient';
import properties from 'constants/ApplicationConstants';
import Tooltip from '@mui/material/Tooltip';
import FinanceClearanceUpdateForm from './FinanceClearanceUpdateForm';

const FinanceClearanceList = () => {
  const [financeClearances, setFinanceClearances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedClearance, setSelectedClearance] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState('success');
  const [snackMessage, setSnackMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchParams, setSearchParams] = useState({
    employeeId: '',
    employeeName: '',
    status: '',
  });

  const [managerSignature, setManagerSignature] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [errors, setErrors] = useState({});

  const handleSnackClose = () => setSnackOpen(false);

  // Status options for dropdown
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    // { value: 'REJECTED', label: 'Rejected' },
    // { value: 'CLEARED', label: 'Cleared' },
  ];

  // Signature Upload Handler
  const handleSignatureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setSnackMessage('File size should not exceed 2MB');
        setSnackColor('error');
        setSnackOpen(true);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setManagerSignature(reader.result);
        if (errors.signature) {
          setErrors({ ...errors, signature: '' });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Signature Remove Handler
  const handleSignatureRemove = () => {
    setManagerSignature(null);
  };

  // Signature Upload Component
  const SignatureUpload = ({ label, error, helperText }) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" fontWeight="medium" mb={1}>
        {label}
        <span style={{ color: properties.colors.Red }}>*</span>
      </Typography>
      {managerSignature ? (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={managerSignature}
            alt="Manager Signature"
            style={{
              maxWidth: '200px',
              maxHeight: '100px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
          />
          <IconButton
            size="small"
            onClick={handleSignatureRemove}
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: 'background.paper',
            }}
          >
            <Delete fontSize="small" color="error" />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={<CloudUpload />}
          size="small"
          sx={{
            color: 'rgba(202, 24, 24, 0.52)'
          }}
        >
          Upload Signature
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleSignatureUpload}
          />
        </Button>
      )}
      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );

  // Transform paginated data with sequential IDs
  const transformPaginatedData = (paginatedData, currentPage, pageSize) => {
    return paginatedData.map((item, index) => ({
      ...item,
      id: currentPage * pageSize + index + 1,
    }));
  };

  const fetchAllFinanceClearances = async (
    page = 0,
    pageSize = 10,
    employeeId = '',
    employeeName = '',
    status = ''
  ) => {
    setLoading(true);
    try {
      const jwtToken = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('size', pageSize);

      // Combine employeeId and employeeName into single search parameter
      const searchTerms = [];
      if (employeeId.trim()) searchTerms.push(employeeId.trim());
      if (employeeName.trim()) searchTerms.push(employeeName.trim());
      if (searchTerms.length > 0) {
        params.append('search', searchTerms.join(' '));
      }

      if (status.trim()) params.append('status', status.trim());

      const response = await GET(`api/exit/clearance/finance?${params.toString()}`, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        const { data } = response;
        const paginatedData = data.data?.paginatedData || [];
        const transformedData = transformPaginatedData(paginatedData, page, pageSize);

        setFinanceClearances(transformedData);
        setTotalItems(data.data?.totalItems || 0);
        setTotalPages(data.data?.totalPages || 0);
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || 'Failed to load Finance clearances');
      setSnackColor('error');
      setSnackOpen(true);
      setFinanceClearances([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    setPaginationModel({ ...paginationModel, page: 0 });
    await fetchAllFinanceClearances(
      0,
      paginationModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName,
      searchParams.status
    );
    setSearchLoading(false);
    setSnackMessage('Search completed successfully');
    setSnackColor('success');
    setSnackOpen(true);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setSearchParams({ employeeId: '', employeeName: '', status: '' });
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchAllFinanceClearances(0, 10, '', '', '');
  };

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    fetchAllFinanceClearances(
      newModel.page,
      newModel.pageSize,
      searchParams.employeeId,
      searchParams.employeeName,
      searchParams.status
    );
  };

  const handleView = (row) => {
    setSelectedClearance(row);
    setOpenViewModal(true);
  };

  const handleUpdate = (row) => {
    setSelectedClearance(row);
    setOpenUpdateModal(true);
  };

  const handleApprove = (row) => {
    setSelectedClearance(row);
    setManagerSignature(null);
    setErrors({});
    setOpenApproveModal(true);
  };

  const handleReject = (row) => {
    setSelectedClearance(row);
    setManagerSignature(null);
    setRejectReason('');
    setErrors({});
    setOpenRejectModal(true);
  };

  const submitApprove = async () => {
    if (!managerSignature) {
      setErrors({ signature: 'Manager signature is required' });
      return;
    }

    setActionLoading(true);
    try {
      const jwtToken = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');
      const requestBody = { managerSignature: managerSignature };
      const response = await POST(
        `api/exit/clearance/finance/${selectedClearance.exitClearanceId}/approve`,
        requestBody,
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );

      if (response.status === 200) {
        setSnackMessage('Finance clearance approved successfully');
        setSnackColor('success');
        setSnackOpen(true);
        setOpenApproveModal(false);
        fetchAllFinanceClearances(
          paginationModel.page,
          paginationModel.pageSize,
          searchParams.employeeId,
          searchParams.employeeName,
          searchParams.status
        );
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || 'Failed to approve Finance clearance');
      setSnackColor('error');
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const submitReject = async () => {
    if (!managerSignature) {
      setErrors({ signature: 'Manager signature is required' });
      return;
    }

    setActionLoading(true);
    try {
      const jwtToken = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');
      const requestBody = {
        managerSignature: managerSignature,
        reason: rejectReason.trim() || null,
      };
      const response = await POST(
        `api/exit/clearance/finance/${selectedClearance.exitClearanceId}/reject`,
        requestBody,
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );

      if (response.status === 200) {
        setSnackMessage('Finance clearance rejected successfully');
        setSnackColor('success');
        setSnackOpen(true);
        setOpenRejectModal(false);
        fetchAllFinanceClearances(
          paginationModel.page,
          paginationModel.pageSize,
          searchParams.employeeId,
          searchParams.employeeName,
          searchParams.status
        );
      }
    } catch (error) {
      setSnackMessage(error.response?.data?.message || 'Failed to reject Finance clearance');
      setSnackColor('error');
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchAllFinanceClearances(0, 10, '', '', '');
  }, []);

  const columns = [
    {
      field: 'id',
      headerName: '#',
      width: 70,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'employeeId',
      headerName: 'Employee ID',
      width: 130,
      headerAlign: 'center',
      align: 'center',
    },
    { field: 'employeeName', headerName: 'Employee Name', width: 200 },
    {
      field: 'status',
      headerName: 'Clearance Status',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = params.value?.toLowerCase() || '';
        let buttonColor;
        let statusText = params.value || 'N/A';

        switch (status) {
          case 'pending':
            buttonColor = 'warning';
            break;
          case 'approved':
          case 'cleared':
            buttonColor = 'success';
            break;
          case 'rejected':
            buttonColor = 'error';
            break;
          default:
            buttonColor = 'default';
        }

        return (
          <MDButton
            variant="contained"
            color={buttonColor}
            size="small"
            disabled
            sx={{
              textTransform: 'capitalize',
              fontSize: '0.800rem',
              width: '100px',
              justifyContent: 'center',
            }}
          >
            {statusText}
          </MDButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      type: 'actions',
      getActions: (params) => {
        const status = params.row.status?.toLowerCase() || '';
        const actions = [];

        actions.push(
          <Tooltip title="View Form" arrow key="view">
            <GridActionsCellItem
              icon={<VisibilityIcon />}
              label="View"
              color="primary"
              onClick={() => handleView(params.row)}
            />
          </Tooltip>
        );

        if (status === 'pending' || status === 'approved' || status === 'cleared') {
          actions.push(
            <Tooltip title="Update Form" arrow key="update">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Update"
                color="info"
                onClick={() => handleUpdate(params.row)}
              />
            </Tooltip>
          );
        }

        // if (status === 'pending') {
        //   actions.push(
        //     <Tooltip title="Approve" arrow key="approve">
        //       <GridActionsCellItem
        //         icon={<CheckCircleIcon />}
        //         label="Approve"
        //         color="success"
        //         onClick={() => handleApprove(params.row)}
        //       />
        //     </Tooltip>
        //   );

        //   actions.push(
        //     <Tooltip title="Reject" arrow key="reject">
        //       <GridActionsCellItem
        //         icon={<CancelIcon />}
        //         label="Reject"
        //         color="error"
        //         onClick={() => handleReject(params.row)}
        //       />
        //     </Tooltip>
        //   );
        // }

        return actions;
      },
    },
  ];

 const getStatusChip = (status) => {
     if (!status) return <Chip label="N/A" size="small" />;
 
     const s = status.toUpperCase();
     let color = 'default';
 
     if (s === 'YES' || s === 'SUBMITTED') color = 'success';
     else if (s === 'NO' || s === 'PENDING') color = 'warning';
     else if (s === 'NA' || s === 'NOT_REQUIRED') color = 'info';
 
     return <Chip label={status} color={color} size="small" />;
   };

  if (loading && paginationModel.page === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <Card>
          <MDBox
            variant="gradient"
            sx={{ background: properties.colors.bgcolorbluethem }}
            bgColor="info"
            borderRadius="lg"
            coloredShadow="success"
            mx={2}
            mt={2}
            p={3}
            mb={1}
            textAlign="center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              Finance Clearance Management
            </MDTypography>
            <MDTypography variant="body2" color="white" opacity={0.8}>
              View and manage Finance clearance records
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            {/* Search Card */}
            <Card sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Search Finance Clearance
              </MDTypography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee ID"
                    variant="standard"
                    value={searchParams.employeeId}
                    onChange={(e) => setSearchParams({ ...searchParams, employeeId: e.target.value })}
                    placeholder="Enter Employee ID"
                    onKeyPress={handleKeyPress}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth
                    type="text"
                    label="Employee Name"
                    variant="standard"
                    value={searchParams.employeeName}
                    onChange={(e) => setSearchParams({ ...searchParams, employeeName: e.target.value })}
                    placeholder="Enter Employee Name"
                    onKeyPress={handleKeyPress}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={searchParams.status}
                      onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                      label="Filter by Status"
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={searchLoading}
                      sx={{ background: properties.colors.bgcolorbluethem }}
                    >
                      {searchLoading ? 'Searching...' : 'Search'}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                    >
                      Reset
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>

            {/* DataGrid with Server-side Pagination */}
            <div style={{ height: 500, width: '100%' }}>
              <DataGrid
                rows={financeClearances}
                columns={columns}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                rowCount={totalItems}
                pageSizeOptions={[10, 25, 50]}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' },
                }}
              />
            </div>

            <MDBox mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="caption" color="text">
                Showing {financeClearances.length} of {totalItems} records
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Page {paginationModel.page + 1} of {totalPages || 1}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {/* View Modal (Read-Only) */}
                <FinanceClearanceUpdateForm
                  open={openViewModal}
                  onClose={() => setOpenViewModal(false)}
                  clearanceData={selectedClearance}
                  readOnly={true}  // This triggers read-only mode
                  onSuccess={() => {
                    setOpenViewModal(false);
                    fetchAllFinanceClearances(
                      paginationModel.page,
                      paginationModel.pageSize,
                      searchParams.employeeId,
                      searchParams.employeeName,
                      searchParams.status
                    );
                  }}
                />
        
                {/* Update Modal (Editable) */}
                <FinanceClearanceUpdateForm
                  open={openUpdateModal}
                  onClose={() => setOpenUpdateModal(false)}
                  clearanceData={selectedClearance}
                  readOnly={false}  // or omit (default false)
                  onSuccess={() => {
                    setOpenUpdateModal(false);
                    fetchAllFinanceClearances(
                      paginationModel.page,
                      paginationModel.pageSize,
                      searchParams.employeeId,
                      searchParams.employeeName,
                      searchParams.status
                    );
                  }}
                />

        {/* Snackbar */}
        <Snackbar
          open={snackOpen}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          onClose={handleSnackClose}
        >
          <Alert
            onClose={handleSnackClose}
            severity={snackColor === 'success' ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%', color: '#fff' }}
          >
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default FinanceClearanceList;